<template>
  <section v-show="status !== 'ready' || matches.length" class="recording-check" aria-label="Recording check">
    <p class="font-semibold">Uploading to {{ destination }}</p>
    <p class="break-all">{{ file.name }}</p>
    <div role="status" aria-live="polite" class="mt-2">
      <p v-if="checking">Checking this recording… You can preview it and set timings while we check.</p>
      <p v-else-if="status === 'unavailable'">Duplicate check unavailable. You can retry or acknowledge this before uploading.</p>
      <p v-else-if="status === 'blocked'">This recording could not be checked. Please refresh the page and check your access before uploading.</p>
      <p v-else-if="crossCeremonyMatches.length">This looks like a recording you’ve already uploaded. We’ll ask you to review the match before uploading.</p>
      <p v-else-if="matches.length">This recording has already been uploaded to this ceremony. Current status: {{ matches[0].status }}. You can continue through the usual retry or replacement steps.</p>
    </div>
    <button v-if="status === 'unavailable'" type="button" class="check-secondary mt-2" @click="runCheck">Retry check</button>
    <dialog ref="confirmation" aria-labelledby="recording-confirm-title" @keydown="trapConfirmationTab" @cancel.prevent="finishConfirmation(null)">
      <h2 id="recording-confirm-title" class="text-xl font-bold">
        {{ status === 'unavailable' ? 'Duplicate check unavailable' : 'This looks like a recording you’ve already uploaded' }}
      </h2>
      <p v-if="status === 'unavailable'" class="mt-3">We couldn’t check this recording against previous uploads. Check that you have selected the right file before continuing.</p>
      <template v-else>
        <p class="mt-3">Parts of this file match recordings used for:</p>
        <ul class="mt-2 list-disc pl-5">
          <li v-for="match in crossCeremonyMatches" :key="match.sourceId">{{ formatCeremony(match) }}</li>
        </ul>
      </template>
      <p class="mt-3">You’re uploading to <strong>{{ destination }}</strong>.</p>
      <div class="check-actions mt-5">
        <button type="button" class="check-primary" autofocus @click="chooseAnotherFile">Choose another file</button>
        <button type="button" class="check-secondary" @click="continueUpload">{{ status === 'unavailable' ? 'Upload without this check' : 'Upload this recording anyway' }}</button>
        <button type="button" class="check-secondary" @click="finishConfirmation(null)">Cancel</button>
      </div>
    </dialog>
  </section>
</template>

<script>
import axios from 'axios'
import { recordingFingerprint } from '../../admin/recordingFingerprint.js'
import { trapTab } from '../Snapshot/dialogFocus.js'

export default {
  props: {
    file: { type: Object, required: true },
    ceremonyId: { type: String, required: true },
    destination: { type: String, required: true },
    formatCeremony: { type: Function, required: true },
  },
  data() {
    return { status: 'idle', fingerprint: null, matches: [], generation: 0 }
  },
  computed: {
    checking() { return this.status === 'checking' },
    crossCeremonyMatches() { return this.matches.filter(match => match.ceremonyId !== this.ceremonyId) },
  },
  watch: {
    file: { immediate: true, handler: 'runCheck' },
    ceremonyId: 'runCheck',
  },
  beforeDestroy() {
    this.invalidate()
    this.$emit('checking', false)
  },
  methods: {
    trapConfirmationTab(event) {
      trapTab(event, this.$refs.confirmation)
    },
    invalidate() {
      this.generation += 1
      if (this.checkController) this.checkController.abort()
      if (this.cancelRequest) this.cancelRequest()
      this.finishConfirmation(null)
    },
    async runCheck() {
      this.invalidate()
      const generation = this.generation
      this.status = 'checking'
      this.matches = []
      this.fingerprint = null
      this.$emit('checking', true)
      const controller = new AbortController()
      this.checkController = controller
      const cancel = axios.CancelToken.source()
      this.cancelRequest = cancel.cancel
      // Bound stalled local reads as well as network requests.
      const timeout = setTimeout(() => { controller.abort(); cancel.cancel() }, 30000)
      try {
        const fingerprint = await recordingFingerprint(this.file, controller.signal)
        if (generation !== this.generation || controller.signal.aborted) return
        this.fingerprint = fingerprint
        const result = await axios.post(`/ceremonies/${this.ceremonyId}/upload`, {
          action: 'check_recording', fingerprint,
        }, { timeout: 15000, cancelToken: cancel.token })
        if (generation !== this.generation) return
        if (!Array.isArray(result.data.matches)) throw new Error('Invalid recording check response')
        this.matches = result.data.matches
        this.status = 'ready'
      } catch (error) {
        if (generation !== this.generation) return
        this.status = [400, 401, 403, 404].includes(error.response?.status) ? 'blocked' : 'unavailable'
      } finally {
        clearTimeout(timeout)
        if (generation === this.generation) {
          if (this.status === 'checking') this.status = 'unavailable'
          this.$emit('checking', false)
        }
      }
    },
    async confirmUpload() {
      if (this.checking || this.status === 'blocked' || this.status === 'idle') return null
      if (!this.crossCeremonyMatches.length && this.status === 'ready') {
        return { fingerprint: this.fingerprint, outcome: 'clear' }
      }
      if (this.resolveConfirmation) return null
      const result = new Promise(resolve => { this.resolveConfirmation = resolve })
      await this.$nextTick()
      if (this.resolveConfirmation) this.$refs.confirmation.showModal()
      return result
    },
    finishConfirmation(value) {
      if (this.$refs.confirmation?.open) this.$refs.confirmation.close()
      if (this.resolveConfirmation) this.resolveConfirmation(value)
      this.resolveConfirmation = null
    },
    chooseAnotherFile() {
      this.finishConfirmation(null)
      this.$emit('replace')
    },
    continueUpload() {
      this.finishConfirmation({
        fingerprint: this.fingerprint,
        outcome: this.status === 'unavailable' ? 'unavailable' : 'confirmed',
        acknowledgedSourceIds: this.crossCeremonyMatches.map(match => match.sourceId),
      })
    },
    handleUploadError(error) {
      const data = error.response?.data
      if (data?.code === 'recording_match') {
        this.matches = data.matches
        this.status = 'ready'
        return true
      }
      if (data?.code === 'duplicate_check_unavailable') {
        this.status = 'unavailable'
        return true
      }
      return false
    },
  },
}
</script>

<style scoped>
.recording-check { padding: 1rem; border: 1px solid #d1d5db; border-radius: 6px; }
dialog { width: min(36rem, calc(100% - 2rem)); max-height: calc(100% - 2rem); overflow: auto; padding: 1.5rem; border: 0; border-radius: 8px; color: #111827; }
dialog::backdrop { background: rgb(17 24 39 / 60%); }
.check-actions { display: flex; flex-wrap: wrap; gap: .75rem; }
.check-primary, .check-secondary { min-height: 44px; padding: .5rem .75rem; border-radius: 6px; border: 1px solid #6b7280; }
.check-primary { color: white; background: #326ee7; border-color: #326ee7; }
.check-secondary { color: #111827; background: white; }
button:focus-visible { outline: 3px solid #326ee7; outline-offset: 3px; }
</style>
